import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, Slider, Fade, IconButton } from "@mui/material";
import {
  PlayArrow,
  Pause,
  Replay5,
  Forward5,
  Fullscreen,
} from "@mui/icons-material";
import { VolumeUp, VolumeDown, VolumeOff } from "@mui/icons-material";

const formatTime = (time) => {
  const mins = Math.floor(time / 60);
  const secs = Math.floor(time % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const VideoPlayer = ({
  fileUrl,
  showVideoSkipControls = false,
  resetOnWindowChange = true,
  onComplete = () => {},
}) => {
  const videoRef = useRef(null);
  const playerContainerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showPlayPauseAnim, setShowPlayPauseAnim] = useState(null); // 'play' or 'pause'
  const [showRewindAnim, setShowRewindAnim] = useState(false);
  const [showForwardAnim, setShowForwardAnim] = useState(false);
  const [volume, setVolume] = useState(100); // 0 to 100
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const preventSeek = (e) => {
    const video = videoRef.current;
    if (video && e.target.currentTime > video.currentTime + 0.01) {
      video.currentTime = video.currentTime;
    }
  };

  const getVolumeIcon = () => {
    if (volume === 0) return <VolumeOff sx={{ fontSize: 25 }} />;
    if (volume < 50) return <VolumeDown sx={{ fontSize: 25 }} />;
    return <VolumeUp sx={{ fontSize: 25 }} />;
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
    if (videoRef.current) {
      videoRef.current.volume = newValue / 100;
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const isMuted = volume === 0;
      const newVolume = isMuted ? 100 : 0;
      setVolume(newVolume);
      videoRef.current.volume = newVolume / 100;
    }
  };

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
      setShowPlayPauseAnim("play");
    } else {
      video.pause();
      setIsPlaying(false);
      setShowPlayPauseAnim("pause");
    }
    setTimeout(() => setShowPlayPauseAnim(null), 700);
  };

  const handleRewind = () => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = Math.max(video.currentTime - 5, 0);
      setShowRewindAnim(true);
      setTimeout(() => setShowRewindAnim(false), 700);
    }
  };

  const handleForward = () => {
    const video = videoRef.current;
    if (video && showVideoSkipControls) {
      video.currentTime = Math.min(video.currentTime + 5, video.duration);
      setShowForwardAnim(true);
      setTimeout(() => setShowForwardAnim(false), 700);
    }
  };

  const updateTime = () => {
    const video = videoRef.current;
    if (video) {
      setCurrentTime(video.currentTime);
    }
  };

  const setupAutoHideControls = () => {
    clearTimeout(controlsTimeoutRef.current);
    setShowControls(true);
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const toggleFullscreen = () => {
    const container = playerContainerRef.current;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable full-screen mode:", err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.addEventListener("timeupdate", updateTime);
      video.addEventListener(
        "loadedmetadata",
        () => setDuration(video.duration),
        (video.volume = volume / 100)
      );
      return () => {
        video.removeEventListener("timeupdate", updateTime);
      };
    }
  }, []);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA"
      )
        return;

      if (e.code === "Space") {
        e.preventDefault();
        handlePlayPause();
      } else if (e.code === "ArrowLeft") {
        e.preventDefault();
        handleRewind();
      } else if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.code === "ArrowRight" && showVideoSkipControls) {
        e.preventDefault();
        handleForward();
      }
    };

    const handleClick = (e) => {
      // Only toggle play/pause on left click (button === 0)
      if (e.button === 0 && playerContainerRef.current?.contains(e.target)) {
        // Avoid toggle if clicking on control buttons (icons, sliders, etc.)
        const tag = e.target.tagName;
        if (
          tag !== "BUTTON" &&
          tag !== "svg" &&
          tag !== "path" &&
          !e.target.closest(".MuiSlider-root")
        ) {
          handlePlayPause();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    const resetVideoOnLeave = () => {
      const video = videoRef.current;
      if (video && resetOnWindowChange) {
        video.currentTime = 0;
        video.pause();
        setIsPlaying(false);
      }
    };
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) resetVideoOnLeave();
    });
    window.addEventListener("blur", resetVideoOnLeave);
    return () => {
      window.removeEventListener("blur", resetVideoOnLeave);
    };
  }, []);

  return (
    <Box sx={{ p: 4, maxWidth: 900, mx: "auto" }}>
      <Box
        ref={playerContainerRef}
        onMouseMove={setupAutoHideControls}
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          mb: 3,
        }}
      >
        <video
          ref={videoRef}
          src={fileUrl}
          width="100%"
          autoPlay
          onEnded={onComplete}
          onSeeking={preventSeek}
          controls={false}
          disablePictureInPicture
          controlsList="nodownload noplaybackrate nofullscreen noremoteplayback"
          onContextMenu={(e) => e.preventDefault()}
        />

        {/* Center Play/Pause Animation */}
        {showPlayPauseAnim && (
          <Box
            sx={{
              position: "absolute",
              top: "40%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: 80,
              opacity: 0.8,
              color: "#fff",
              pointerEvents: "none",
            }}
          >
            {showPlayPauseAnim === "play" ? (
              <PlayArrow fontSize="inherit" />
            ) : (
              <Pause fontSize="inherit" />
            )}
          </Box>
        )}

        {/* Rewind Animation */}
        {showRewindAnim && (
          <Box
            sx={{
              position: "absolute",
              top: "45%",
              left: "5%",
              fontSize: 60,
              opacity: 0.8,
              color: "#fff",
              pointerEvents: "none",
            }}
          >
            <Replay5 fontSize="inherit" />
          </Box>
        )}

        {showForwardAnim && (
          <Box
            sx={{
              position: "absolute",
              top: "45%",
              right: "5%",
              fontSize: 60,
              opacity: 0.8,
              color: "#fff",
              pointerEvents: "none",
            }}
          >
            <Forward5 fontSize="inherit" />
          </Box>
        )}

        {/* Video Controls */}
        <Fade in={showControls}>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              width: "100%",
              bgcolor: "rgba(0,0,0,0.6)",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              px: 0,
              py: 0.5,
            }}
          >
            <Slider
              value={(currentTime / duration) * 100 || 0}
              disabled
              size="small"
              sx={{
                width: "90%",
                color: "white",
                "& .MuiSlider-thumb": { display: "none" },
              }}
            />

            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
              {/* Main Controls - Centered */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  justifyContent: "center",
                  alignItems: "center",
                  width: document.fullscreenElement ? "100%" : "110%",
                  right: 0,
                }}
              >
                <IconButton
                  onClick={handleRewind}
                  sx={{
                    color: "#fff",
                    ":hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
                  }}
                >
                  <Replay5 sx={{ fontSize: 25 }} />
                </IconButton>
                <IconButton
                  onClick={handlePlayPause}
                  sx={{
                    color: "#fff",
                    ":hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
                  }}
                >
                  {isPlaying ? (
                    <Pause sx={{ fontSize: 25 }} />
                  ) : (
                    <PlayArrow sx={{ fontSize: 25 }} />
                  )}
                </IconButton>
                <IconButton
                  onClick={handleForward}
                  disabled={!showVideoSkipControls}
                  sx={{
                    color: "#fff",
                    ":hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
                  }}
                >
                  <Forward5 sx={{ fontSize: 25 }} />
                </IconButton>
                <Box
                  onMouseEnter={() => setShowVolumeSlider(true)}
                  onMouseLeave={() => setShowVolumeSlider(false)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: "120px",
                    height: "30px",
                    overflow: "hidden",
                    borderRadius: "20px",
                    backgroundColor: showVolumeSlider
                      ? "rgba(0, 0, 0, 0.7)"
                      : "transparent",
                    transition: "all 0.3s ease-out",
                    pr: 1,
                  }}
                >
                  {/* Volume Icon */}
                  <IconButton
                    onClick={toggleMute}
                    sx={{
                      width: 25,
                      height: 25,
                      color: "white",
                      flexShrink: 0,
                      "&:hover": {
                        color: "#d1d5db",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {getVolumeIcon()}
                  </IconButton>
                  {/* Volume Slider */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s ease-out",
                      width: showVolumeSlider ? "95px" : "0px",
                      opacity: showVolumeSlider ? 1 : 0,
                    }}
                  >
                    <Slider
                      value={volume}
                      onChange={handleVolumeChange}
                      min={0}
                      max={100}
                      sx={{
                        width: "100%",
                        height: 4,
                        mx: 1,
                        "& .MuiSlider-thumb": {
                          width: 14,
                          height: 14,
                          backgroundColor: "white",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                          "&:hover": {
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.4)",
                          },
                        },
                        "& .MuiSlider-track": {
                          backgroundColor: "white",
                          border: "none",
                        },
                        "& .MuiSlider-rail": {
                          backgroundColor: "#6b7280",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Time and Fullscreen - Positioned absolutely on the right */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#fff", ml: 1, alignSelf: "center" }}
                >
                  {formatTime(currentTime)} / {formatTime(duration)}
                </Typography>

                {/* Fullscreen Button */}
                <IconButton
                  onClick={toggleFullscreen}
                  sx={{
                    color: "#fff",
                    ":hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
                  }}
                >
                  <Fullscreen sx={{ fontSize: 25 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default VideoPlayer;
